import {Paths} from "../types/Paths";
import readNestedProperty from "./readNestedProperty";

interface WritePathInfo {
	parentPath: Paths<any>;
	finalKey: string;
}

const keyTest = /^\d+$/;

function analyzeWritePath(path: string): WritePathInfo {
	const dotIndex = path.lastIndexOf(".");

	if (dotIndex === -1) {
		// No parent path - direct property on a model
		return {
			parentPath: "",
			finalKey: path
		};
	}

	return {
		parentPath: path.substring(0, dotIndex),
		finalKey: path.substring(dotIndex + 1)
	};
}

/**
 * Writes a value to a nested property in an object using a dot notation path (e.g., "user.address.city").
 * Supports both plain properties and setter functions (plain assignment invokes prototype
 * setters with normal semantics — and stays correct when the parent object is later replaced
 * by an instance of a different class, which a cached setter function would not).
 * Automatically creates intermediate objects if they don't exist (auto-vivification).
 * @param model The object to write to.
 * @param path The dot-notation path to the property (e.g., "user.address.city").
 * @param value The value to set at the specified path.
 */
export default function writeNestedProperty<T>(model: T, path: Paths<T>, value: any): void {
	if (!path) return;

	const writeInfo = analyzeWritePath(path);

	let parent: any;
	if (writeInfo.parentPath) {
		parent = readNestedProperty(model, writeInfo.parentPath as Paths<T>);

		// If a parent doesn't exist, create intermediate objects (POCO behavior)
		if (parent == null) {
			const segments = writeInfo.parentPath.split(".");
			parent = model;
			for (let i = 0; i < segments.length; i++) {
				const key = segments[i];
				if (!(key in parent) || parent[key] == null) {
					// Array vs object depends on the NEXT segment: "items.0.name" needs
					// items to be an array and items[0] to be an object.
					const nextKey = i + 1 < segments.length ? segments[i + 1] : writeInfo.finalKey;
					parent[key] = keyTest.test(nextKey) ? [] : {};
				}
				parent = parent[key];
			}
		}
	} else {
		parent = model;
	}

	parent[writeInfo.finalKey] = value;
}
